import React from "react";
import { useAppSelector, useAppDispatch } from "@/store";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, Calendar, Clock, MapPin } from "lucide-react";
import { flowRestarted } from "../publicBookingFlowSlice";
import { formatAppointmentTime, formatCheckInDateTime } from "../checkInTime";

function getNotificationCopy(hasEmail: boolean, hasPhone: boolean): string {
  if (hasEmail && hasPhone) {
    return "Your appointment details have been sent to your email and phone on file.";
  }
  if (hasEmail) {
    return "Your appointment details have been sent to your email on file.";
  }
  if (hasPhone) {
    return "Your appointment details have been sent to your phone on file.";
  }
  return "Please save your confirmation number below — it's your record of this appointment.";
}

export const ConfirmationStep = () => {
  const dispatch = useAppDispatch();
  const booking = useAppSelector(state => state.publicBookingFlow.confirmedBooking);
  const site = useAppSelector(state => state.publicBookingFlow.selectedSite);
  const customerData = useAppSelector(state => state.publicBookingFlow.customerData);

  if (!booking || !site) return null;

  // Site local time, not the visitor's: a booking made from outside the region must still show
  // the time the site expects the applicant to arrive.
  const checkInDateTime = formatCheckInDateTime(booking.start);
  const appointmentTime = formatAppointmentTime(booking.start);

  const notificationCopy = getNotificationCopy(!!customerData?.email, !!customerData?.phone);

  return (
    <Card className="w-full max-w-xl mx-auto border-none shadow-none sm:border-solid sm:shadow-sm">
      <CardHeader className="text-center px-4 space-y-4">
        <div className="mx-auto w-16 h-16 bg-emerald-100 flex items-center justify-center rounded-full">
          <CheckCircle2 className="w-8 h-8 text-emerald-600" />
        </div>
        <CardTitle className="text-2xl font-semibold text-slate-900">Appointment Confirmed</CardTitle>
        <CardDescription className="text-slate-500 text-base space-y-2">
          <p>{notificationCopy}</p>
          <p className="font-semibold text-slate-500">
            Please write this confirmation number and bring it with you to your appointment.
          </p>
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        <div className="bg-slate-50 p-5 rounded-lg border space-y-4">
          <div className="flex flex-col">
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Confirmation Number</span>
            <span className="text-xl font-bold tracking-tight mt-0.5">{booking.confirmationCode}</span>
          </div>

          <div className="h-px w-full bg-slate-200" />

          <div className="flex gap-3">
            <Clock className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <div className="flex flex-col">
              <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Check-In Time</span>
              <span className="font-bold text-base text-slate-900 mt-0.5">{checkInDateTime}</span>
            </div>
          </div>

          <div className="flex gap-3">
            <Calendar className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
            <div className="flex flex-col">
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Appointment Time</span>
              <span className="font-medium text-sm mt-0.5">{appointmentTime}</span>
            </div>
          </div>

          <div className="flex gap-3">
            <MapPin className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
            <div className="flex flex-col">
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Location</span>
              <span className="font-medium text-sm mt-0.5">{site.address}</span>
            </div>
          </div>
        </div>
      </CardContent>

      <CardFooter className="px-4 pb-6 flex flex-col gap-3 w-full max-w-md mx-auto">
        <Button variant="ghost" className="w-full text-slate-400" onClick={() => dispatch(flowRestarted())}>
          Return to Start
        </Button>
      </CardFooter>
    </Card>
  );
}
